import React, { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { padStart } from 'lodash';
import PrepTimeControls from './PrepTimeControls';
import { ORDER_STATUS } from '@baemingo/order-status-events/dist/types';
import OrdersDayOrMoreAway from '../OrdersDayOrMoreAway';
import { EnrichedOrder } from '~Scenes/EventedKitchenDisplay/view';
import { getCompanyData_Company_courseTypes } from '~Scenes/EventedKitchenDisplay/__queries__';
import { ItemWithCourseType } from '~Scenes/EventedKitchenDisplay';
import { useButton, useI18n, useLicenses } from '~Hooks';
import { useStations } from '~Hooks/useStations';
import { useStorage } from '~Components/Storage';
import { TransparentButton } from '~Styles';
import Icon from '~ComponentsImproved/Icon';
import ReceiptLongIcon from '~Assets/icons/receipt_long.svg';

const HeadBarContainer = styled.div`
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  height: 64px;
  border-bottom: 1px solid var(--colorBlueGray1000);
  width: 100%;
`;

const ContentContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 0 16px;
`;

const LeftContentContainer = styled(ContentContainer)`
  flex: 2 2;
`;
const CenterContentContainer = styled(ContentContainer)`
  flex: 1 1;
  justify-content: center;
`;
const RightContentContainer = styled(ContentContainer)`
  flex: 2 2;
  justify-content: flex-end;
`;

const Clock = styled.div`
  font-size: 22px;
  color: #aeb9c8;
`;

const SummaryToggle = styled(TransparentButton)<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ active }) => (active ? 'var(--colorWhite)' : 'var(--colorBlueGray500)')};
  background: ${({ active }) => (active ? 'var(--colorBlueGray1000)' : 'transparent')};
`;

interface Props {
  loading: {
    isLoading: boolean;
    progress: number;
  };
  ordersDayOrMoreAway: EnrichedOrder[];
  venueId: string;
  courseTypes: getCompanyData_Company_courseTypes[];
  itemsWithCourseType: ItemWithCourseType[];
  isModal?: boolean;
}

const HeaderBar: FC<Props> = ({
  loading,
  ordersDayOrMoreAway,
  venueId,
  courseTypes,
  itemsWithCourseType,
  isModal,
}) => {
  const [date, setDate] = useState(new Date());
  const stations = useStations();
  const licenses = useLicenses();
  const { i18n } = useI18n();
  const [storage, updateStorage] = useStorage();

  // Update the date property to progress the clock
  useEffect(() => {
    const timer = setInterval(() => setDate(new Date()), 5000);
    return function cleanup() {
      clearInterval(timer);
    };
  }, []);

  // Check if the selected status has order status pending, empty means all statuses are selected. Also check if the company has app orders otherwise don't show the controls
  const showPrepTimeControls =
    licenses.hasApp &&
    (!stations.selected ||
      stations.selected?.orderStatuses.length === 0 ||
      stations.selected?.orderStatuses.includes(ORDER_STATUS.PENDING));

  const summaryButton = useButton({
    onClick: () => updateStorage({ KDSSummaryPanel: !storage.KDSSummaryPanel }),
  });

  return (
    <HeadBarContainer>
      <LeftContentContainer>
        {!isModal && (
          <OrdersDayOrMoreAway
            ordersDayOrMoreAway={ordersDayOrMoreAway}
            venueId={venueId}
            courseTypes={courseTypes}
            itemsWithCourseType={itemsWithCourseType}
          />
        )}
        {loading.isLoading && <span>Loading: {loading.progress}%</span>}
      </LeftContentContainer>
      <CenterContentContainer>
        <Clock>
          {padStart(date.getHours().toString(), 2, '0') +
            ':' +
            padStart(date.getMinutes().toString(), 2, '0')}
        </Clock>
      </CenterContentContainer>
      <RightContentContainer>
        {showPrepTimeControls && <PrepTimeControls />}
        <SummaryToggle {...summaryButton} active={storage.KDSSummaryPanel}>
          <Icon src={ReceiptLongIcon} height="20px" width="20px" color={storage.KDSSummaryPanel ? 'var(--colorWhite)' : 'var(--colorBlueGray500)'} />
          {i18n('Settings.KDS.SummaryPanel') || 'Summary'}
        </SummaryToggle>
      </RightContentContainer>
    </HeadBarContainer>
  );
};

export default HeaderBar;
